import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import {
  createFeedbackFormApi,
  updateFeedbackFormApi,
  deleteFeedbackFormApi,
} from '../api/formEditorApi'
import { type FeedbackForm } from '../../Item/utils/types'
import { type APIFeedbackFormRequest } from '../../../generated-types/models'
import LogTool from '../../../logger/logTools'
import { feedbackFormKeys } from '../utils/queryKeys'
import { getPKfromSelf } from '../../../utils/functions'

const log = new LogTool({ context: 'FormEditorMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a feedback form
 */
export function useCreateFeedbackForm(): UseMutationResult<
  FeedbackForm,
  Error,
  APIFeedbackFormRequest | any
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createFeedbackFormApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: feedbackFormKeys.lists() })
      log.info('Feedback form created successfully')
    },
    onError: (error) => {
      log.error('Error creating feedback form:', error)
    },
  })
}

/**
 * Hook to update a feedback form
 */
export function useUpdateFeedbackForm(): UseMutationResult<
  FeedbackForm,
  Error,
  { idOrSelf: string | number; data: APIFeedbackFormRequest | any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateFeedbackFormApi(idOrSelf, data),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: feedbackFormKeys.lists() })
      await queryClient.invalidateQueries({ 
        queryKey: feedbackFormKeys.detail(typeof variables.idOrSelf === 'number' ? variables.idOrSelf : Number(getPKfromSelf(String(variables.idOrSelf))))
      })
      log.info('Feedback form updated successfully')
    },
    onError: (error) => {
      log.error('Error updating feedback form:', error)
    },
  })
}

/**
 * Hook to delete a feedback form
 */
export function useDeleteFeedbackForm(): UseMutationResult<
  void,
  Error,
  string | number
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteFeedbackFormApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: feedbackFormKeys.lists() })
      log.info('Feedback form deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting feedback form:', error)
    },
  })
}

